/**
 * TagIt.js - A JavaScript library for managing tag suggestions
 * in contentEditable and input/textarea elements.
 *
 * Features:
 *  - Configurable trigger character.
 *  - Middleware for transforming/filtering suggestions.
 *  - Asynchronous suggestion fetching with debouncing.
 *  - Real-time suggestion management (add/remove).
 *  - Scoring-based suggestion sorting and filtering.
 *
 * @author
 * Deepansu Mor
 * @github https://github.com/deepansumor
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { getCaretCoordinates, getLineHeight, sortAndFilterSuggestions } from './utils';
export default class TagIt {
    /**
     * Constructor for initializing the TagIt class.
     * @param {HTMLElement} target - The HTML element (contentEditable or input/textarea) to attach TagIt to.
     * @param {TagItOptions} [options] - Configuration options for TagIt.
     */
    constructor(target, options) {
        var _a, _b, _c, _d, _e, _f;
        this.triggerChar = '@';
        this.savedRange = null;
        this.middlewares = [];
        this.fetchTimeout = null;
        this.target = target;
        this.suggestions = (options === null || options === void 0 ? void 0 : options.suggestions) || [];
        this.keepTrigger = (_a = options === null || options === void 0 ? void 0 : options.keepTrigger) !== null && _a !== void 0 ? _a : false;
        this.triggerChar = (_b = options === null || options === void 0 ? void 0 : options.triggerChar) !== null && _b !== void 0 ? _b : this.triggerChar;
        this.maxSuggestions = (_c = options === null || options === void 0 ? void 0 : options.maxSuggestions) !== null && _c !== void 0 ? _c : 5;
        this.minScore = (_d = options === null || options === void 0 ? void 0 : options.minScore) !== null && _d !== void 0 ? _d : 0;
        this.debounceTime = (_e = options === null || options === void 0 ? void 0 : options.debounceTime) !== null && _e !== void 0 ? _e : 300;
        this.enableLog = (_f = options === null || options === void 0 ? void 0 : options.enableLog) !== null && _f !== void 0 ? _f : false;
        this.fetchSuggestions = options === null || options === void 0 ? void 0 : options.fetchSuggestions;
        this.dropdown = this.createDropdown();
        this.attachEvents();
    }
    /**
     * Logs a message if logging is enabled.
     * @param {...any} msg - The message(s) to log.
     */
    log(...msg) {
        this.enableLog && console.log(...msg);
    }
    /**
     * Logs an error message.
     * @param {...any} msg - The error message(s) to log.
     */
    error(...msg) {
        console.error(...msg);
    }
    /**
     * Register a middleware function to transform/filter suggestions.
     * @param {SuggestionMiddleware} middleware - The middleware function.
     */
    use(middleware) {
        this.middlewares.push(middleware);
    }
    /**
     * Adds a new suggestion to the instance in real-time.
     * @param {SuggestionItem} suggestion - The suggestion object to add.
     */
    addSuggestion(suggestion) {
        this.suggestions.push(suggestion);
    }
    /**
     * Removes a suggestion by its key.
     * @param {string} key - The key of the suggestion to remove.
     */
    removeSuggestion(key) {
        this.suggestions = this.suggestions.filter(item => item.key !== key);
    }
    /**
     * Creates and returns the dropdown element.
     * @returns {HTMLUListElement} The dropdown element.
     */
    createDropdown() {
        const dropdown = document.createElement('ul');
        Object.assign(dropdown.style, {
            position: 'fixed',
            display: 'none',
            listStyle: 'none',
            padding: '5px',
            margin: '0',
            border: '1px solid #ccc',
            background: '#fff'
        });
        document.body.appendChild(dropdown);
        return dropdown;
    }
    /**
     * Attaches keyup and click events to the target and document.
     */
    attachEvents() {
        this.target.addEventListener('keyup', this.onKeyUp.bind(this));
        document.addEventListener('click', this.onOutsideClick.bind(this));
    }
    /**
     * Handler for clicks outside the dropdown.
     * Hides the dropdown if clicked outside.
     * @param {MouseEvent} e - The click event.
     */
    onOutsideClick(e) {
        if (!this.dropdown.contains(e.target)) {
            this.hideDropdown();
        }
    }
    /**
     * Handler for keyup events on the target element.
     * Debounces async fetching of suggestions and displays the dropdown.
     */
    onKeyUp(e) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const selection = window.getSelection();
                if (selection && selection.rangeCount > 0) {
                    this.savedRange = selection.getRangeAt(0).cloneRange();
                }
                if (this.isTriggerActive()) {
                    const position = getCaretCoordinates(this.target);
                    if (this.fetchSuggestions) {
                        if (this.fetchTimeout) {
                            clearTimeout(this.fetchTimeout);
                        }
                        this.fetchTimeout = window.setTimeout(() => __awaiter(this, void 0, void 0, function* () {
                            try {
                                const asyncSuggestions = yield this.fetchSuggestions();
                                this.suggestions = asyncSuggestions;
                                this.showDropdown(position);
                            }
                            catch (error) {
                                this.error('Error fetching suggestions:', error);
                            }
                        }), this.debounceTime);
                    }
                    else {
                        this.showDropdown(position);
                    }
                }
                else {
                    if (this.fetchTimeout) {
                        clearTimeout(this.fetchTimeout);
                        this.fetchTimeout = null;
                    }
                    this.hideDropdown();
                }
            }
            catch (err) {
                this.error("Error in onKeyUp:", err);
            }
        });
    }
    // --- Helpers for Input/TextArea vs. ContentEditable ---
    isInputTarget() {
        return this.target instanceof HTMLTextAreaElement || this.target instanceof HTMLInputElement;
    }
    getTextBeforeCaretForInput() {
        const textarea = this.target;
        return textarea.value.substring(0, textarea.selectionStart);
    }
    getTextBeforeCaretForEditable() {
        var _a;
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            const node = range.startContainer;
            if (node.nodeType === Node.TEXT_NODE) {
                return ((_a = node.textContent) === null || _a === void 0 ? void 0 : _a.substring(0, range.startOffset)) || '';
            }
        }
        return '';
    }
    /**
     * Checks if the trigger is active (i.e. the last word before caret starts with the trigger character).
     * @returns {boolean} True if active, false otherwise.
     */
    isTriggerActive() {
        let textBeforeCaret = this.isInputTarget()
            ? this.getTextBeforeCaretForInput()
            : this.getTextBeforeCaretForEditable();
        textBeforeCaret = textBeforeCaret.replace(/\u00A0/g, " ");
        const parts = textBeforeCaret.split(" ");
        const lastPart = parts[parts.length - 1];
        if (!lastPart.startsWith(this.triggerChar)) {
            this.log("Trigger character not found before caret:", lastPart);
        }
        return lastPart.startsWith(this.triggerChar) && this.getTriggerQuery().indexOf(" ") === -1;
    }
    getQueryForInput() {
        const textarea = this.target;
        const start = textarea.selectionStart;
        const value = textarea.value;
        const triggerIndex = value.lastIndexOf(this.triggerChar, start);
        if (triggerIndex === -1) {
            this.log("Trigger character not found in input value");
            return '';
        }
        return value.substring(triggerIndex + 1, start);
    }
    getQueryForEditable() {
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            const node = range.startContainer;
            if (node.nodeType === Node.TEXT_NODE) {
                const textContent = node.textContent || '';
                const triggerIndex = textContent.lastIndexOf(this.triggerChar, range.startOffset);
                if (triggerIndex === -1) {
                    this.log("Trigger character not found in editable content");
                    return '';
                }
                return textContent.substring(triggerIndex + 1, range.startOffset);
            }
        }
        return '';
    }
    getTriggerQuery() {
        return this.isInputTarget() ? this.getQueryForInput() : this.getQueryForEditable();
    }
    // --- Dropdown Rendering and Scoring ---
    /**
     * Renders the suggestion dropdown based on current suggestions and query.
     * @param {Coordinates} position - The coordinates to position the dropdown.
     */
    showDropdown(position) {
        let suggestionsToShow = [...this.suggestions];
        for (const middleware of this.middlewares) {
            try {
                suggestionsToShow = middleware(suggestionsToShow);
            }
            catch (err) {
                this.error("Error in middleware:", err);
            }
        }
        const query = this.getTriggerQuery().toLowerCase();
        suggestionsToShow = sortAndFilterSuggestions(suggestionsToShow, query, this.minScore);
        suggestionsToShow = suggestionsToShow.slice(0, this.maxSuggestions);
        if (suggestionsToShow.length === 0) {
            this.log("No suggestions available for query:", query);
            return this.hideDropdown();
        }
        this.dropdown.innerHTML = '';
        const lineHeight = getLineHeight(this.target);
        suggestionsToShow.forEach(item => {
            const li = document.createElement('li');
            li.textContent = item.display;
            Object.assign(li.style, {
                padding: '3px 6px',
                cursor: 'pointer'
            });
            li.addEventListener('mousedown', (e) => e.preventDefault());
            li.addEventListener('click', () => {
                try {
                    this.insertTag(item.display);
                }
                catch (err) {
                    this.error("Error inserting tag:", err);
                }
                this.hideDropdown();
            });
            this.dropdown.appendChild(li);
        });
        Object.assign(this.dropdown.style, {
            top: `${position.top + lineHeight}px`,
            left: `${position.left + 5}px`,
            display: 'block'
        });
    }
    /**
     * Hides the suggestion dropdown.
     */
    hideDropdown() {
        this.dropdown.style.display = 'none';
    }
    /**
     * Inserts the selected tag into the target element.
     * @param {string} tag - The tag text to insert.
     */
    insertTag(tag) {
        try {
            if (this.isInputTarget()) {
                this.insertTagForTextarea(tag);
            }
            else {
                this.insertTagForContentEditable(tag);
            }
        }
        catch (err) {
            this.error("Error in insertTag:", err);
        }
    }
    /**
     * Inserts the tag into a textarea or input element.
     * @param {string} tag - The tag text to insert.
     */
    insertTagForTextarea(tag) {
        const textarea = this.target;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const value = textarea.value;
        const triggerIndex = value.lastIndexOf(this.triggerChar, start);
        if (triggerIndex === -1) {
            this.log("Trigger character not found when inserting tag in textarea");
            return;
        }
        const beforeTrigger = value.substring(0, triggerIndex);
        const afterCaret = value.substring(end);
        const triggerPart = this.keepTrigger ? this.triggerChar : '';
        const newValue = `${beforeTrigger}${triggerPart}${tag} ${afterCaret}`;
        textarea.value = newValue;
        const newPos = beforeTrigger.length + triggerPart.length + tag.length + 1;
        textarea.setSelectionRange(newPos, newPos);
    }
    /**
     * Inserts the tag into a contentEditable element.
     * @param {string} tag - The tag text to insert.
     */
    insertTagForContentEditable(tag) {
        const selection = window.getSelection();
        if (this.savedRange && selection) {
            selection.removeAllRanges();
            selection.addRange(this.savedRange);
        }
        if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            const node = range.startContainer;
            if (node.nodeType !== Node.TEXT_NODE) {
                this.log("Current node is not a text node; cannot insert tag");
                return;
            }
            const textContent = node.textContent || '';
            const triggerIndex = textContent.lastIndexOf(this.triggerChar, range.startOffset);
            if (triggerIndex === -1) {
                this.log("Trigger character not found in contentEditable text");
                return;
            }
            const before = textContent.substring(0, triggerIndex);
            const after = textContent.substring(range.startOffset);
            const triggerPart = this.keepTrigger ? this.triggerChar : '';
            const insertText = triggerPart + tag + ' ';
            const beforeNode = document.createTextNode(before);
            const tagNode = document.createTextNode(insertText);
            const afterNode = document.createTextNode(after);
            const parent = node.parentNode;
            if (!parent) {
                this.error("Parent node not found; cannot insert tag");
                return;
            }
            parent.insertBefore(beforeNode, node);
            parent.insertBefore(tagNode, node);
            parent.insertBefore(afterNode, node);
            parent.removeChild(node);
            const newRange = document.createRange();
            newRange.setStartAfter(tagNode);
            newRange.collapse(true);
            selection.removeAllRanges();
            selection.addRange(newRange);
        }
    }
    /**
     * Destroys the TagIt instance by removing event listeners and the dropdown element.
     */
    destroy() {
        this.target.removeEventListener('keyup', this.onKeyUp.bind(this));
        document.removeEventListener('click', this.onOutsideClick.bind(this));
        document.body.removeChild(this.dropdown);
    }
}
